import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Instagram, Twitter, Send } from "lucide-react";
import { PrimenaxLogo } from "./Logo";
import { useCart } from "../context/CartContext";
import { CartDrawer } from "./CartDrawer";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/reviews", label: "Reviews" },
  { to: "/about", label: "Brand" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { count, openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" className="shrink-0">
          <PrimenaxLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}-link`}
              className={({ isActive }) =>
                `font-display text-base tracking-wider uppercase transition-colors ${
                  isActive
                    ? "text-[#D10000]"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={openCart}
            data-testid="open-cart-button"
            className="relative inline-flex items-center gap-2 px-3 py-2 border border-white/10 hover:border-[#D10000]/60 transition-colors group"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="font-display text-sm tracking-wider hidden sm:inline">
              CART
            </span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[#D10000] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>

          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/10"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase()}-link`}
                className="font-display text-2xl tracking-wider uppercase text-white/90"
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message || "Subscribed.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className="border-t border-white/10 bg-[#050505] mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-2 md:grid-cols-12 gap-12">
        <div className="col-span-2 md:col-span-4">
          <PrimenaxLogo variant="full" />
          <p className="mt-6 text-white/60 max-w-md font-body leading-relaxed">
            Premium, naturally-formulated supplements engineered for the modern
            man. Performance, drive, dominance — be the natural selection.
          </p>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-sm tracking-[0.3em] text-white/40 mb-5">
            EXPLORE
          </h4>
          <ul className="space-y-3 font-body">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="text-white/80 hover:text-[#D10000] transition-colors"
                  data-testid={`footer-${n.label.toLowerCase()}-link`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-sm tracking-[0.3em] text-white/40 mb-5">
            SUPPORT
          </h4>
          <ul className="space-y-3 font-body text-white/80">
            <li>
              <Link
                to="/track"
                data-testid="footer-track-link"
                className="hover:text-[#D10000]"
              >
                Track Order
              </Link>
            </li>
            <li>
              <Link
                to="/shipping"
                data-testid="footer-shipping-link"
                className="hover:text-[#D10000]"
              >
                Shipping
              </Link>
            </li>
            <li>
              <Link
                to="/returns"
                data-testid="footer-returns-link"
                className="hover:text-[#D10000]"
              >
                Returns & Guarantee
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                data-testid="footer-support-contact-link"
                className="hover:text-[#D10000]"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-display text-sm tracking-[0.3em] text-white/40 mb-5">
            JOIN THE INNER CIRCLE
          </h4>
          <p className="text-white/60 font-body text-sm mb-4">
            Drops, restocks, and members-only offers.
          </p>
          <form onSubmit={onSubscribe} className="flex items-center gap-2" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-transparent border-b border-white/20 focus:border-[#D10000] outline-none py-2 text-white placeholder:text-white/30"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D10000] hover:bg-[#A30000] transition-colors disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              <span className="font-display tracking-wider">JOIN</span>
            </button>
          </form>
          <div className="flex items-center gap-3 mt-8 text-white/60">
            <a href="#" aria-label="Instagram" className="hover:text-white">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="#" aria-label="Twitter" className="hover:text-white">
              <Twitter className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Legal strip */}
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center gap-x-8 gap-y-3 text-white/55 text-xs">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">Terms of Service</Link>
          <Link to="/shipping" data-testid="footer-shipping-link-2" className="hover:text-white">Shipping</Link>
          <Link to="/returns" data-testid="footer-returns-link-2" className="hover:text-white">Returns</Link>
          <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-white">Disclaimer</Link>
          <Link to="/track" data-testid="footer-track-link-2" className="hover:text-white">Order Tracking</Link>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-white/40 text-xs font-body">
          <div className="space-y-1">
            <p>© {new Date().getFullYear()} Primenax. All rights reserved.</p>
            <p>
              Primenax<sup>®</sup> is a registered trademark of QCG Brands
              International, Inc. · 1 Marina Park Dr, Suite 1410, Boston, MA
              02210
            </p>
          </div>
          <p className="md:text-right md:max-w-md">
            * These statements have not been evaluated by the FDA. Not intended
            to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
      <CartDrawer />
    </div>
  );
};

export default Layout;
